import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  BookOpen,
  ChevronDown,
  Compass,
  Eye,
  EyeOff,
  FileText,
  Layers,
  Link2,
  LogIn,
  MessageCircle,
  ShieldCheck,
  Sparkles,
  UserPlus,
  Zap,
} from "lucide-react";
import client, { fetchDemoPersonas, demoLogin, errText, getAccess, setTokens } from "../lib/api";

/**
 * Landing destination for a persona based on the seeded state the
 * backend reported. Admin roles bypass the person chain and land
 * on their console.
 */
function landingForPersona(product, state, role) {
  if (role === "institution_admin" || role === "institution_staff") {
    return "/admin";
  }
  if (role === "content_admin") {
    return "/admin/books";
  }
  if (role === "platform_admin") {
    return "/admin/items";
  }
  const base = product === "grow" ? "/grow" : "";
  if (!state?.consent) return `${base}/consent`;
  if (!state?.intake)  return `${base}/welcome`;
  if (!state?.read)    return `${base}/welcome`;
  return `${base}/report`;
}

// Routes the sign-in flow is willing to resume a person into.
const RESUMABLE_ROUTES = [
  "/consent", "/welcome", "/onboarding/education", "/onboarding/experience",
  "/onboarding/goal", "/klair", "/home", "/report", "/career", "/roadmap",
  "/learning", "/resume", "/resume/shares", "/ask", "/checkin", "/checkin/delta",
];

function isPostReadRoute(route) {
  return /\/(home|report|career|roadmap|learning|resume|ask|checkin)/.test(route || "");
}

/**
 * Decide where a returning person lands. Prefer the exact screen they
 * were last on (start where you left off), but never send them past a
 * gate they have not cleared: a post-read route needs a read, an
 * onboarding route needs consent. Otherwise fall back to the earliest
 * unfinished step.
 */
// Reads the role claim from a JWT access token, without verifying it
// (the server verifies; this is only to route the landing screen).
function roleFromToken(token) {
  try {
    return JSON.parse(atob(String(token).split(".")[1] || "")).role || null;
  } catch (_e) {
    return null;
  }
}

async function resolveLanding(product) {
  const base = product === "grow" ? "/grow" : "";
  try {
    const { data } = await client.get(`/journey?product=${product}`);
    const last = data?.last_route;
    const grow = product === "grow";
    const lastMatchesProduct = last && (grow ? last.startsWith("/grow") : !last.startsWith("/grow"));
    const bare = last ? last.replace(/^\/grow/, "") || "/" : "";
    if (last && lastMatchesProduct && RESUMABLE_ROUTES.includes(bare)) {
      if (isPostReadRoute(last) && data.read) return last;
      if (!isPostReadRoute(last) && data.consent) return last;
    }
    if (!data.consent) return `${base}/consent`;
    if (!data.intake) return `${base}/welcome`;
    if (!data.read) return `${base}/welcome`;
    return `${base}/report`;
  } catch (_e) {
    return `${base}/consent`;
  }
}
const KINETIC_WORDS = [
  "a plan to get there",
  "the words to own it",
  "a resume that lands",
  "proof you can point to",
];

// A small, illustrative sample of what a finished read looks like.
// Clearly tagged as a sample so it never reads as a real result.
const READ_LAYERS = [
  { label: "Drive",  pct: 82, tone: "var(--k-L2)" },
  { label: "Craft",  pct: 90, tone: "var(--k-L6)" },
  { label: "People", pct: 48, tone: "var(--k-L3)" },
  { label: "Risk",   pct: 63, tone: "var(--k-L4)" },
];

// Month names for the date-of-birth picker. Values are 1..12.
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const pad2 = (n) => String(n).padStart(2, "0");

export default function AuthScreen({ product }) {
  const nav = useNavigate();
  const [mode, setMode] = useState("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [dob, setDob] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [demoPersonas, setDemoPersonas] = useState(null);
  const [demoLoading, setDemoLoading] = useState(null); // key currently logging in
  const [demoOpen, setDemoOpen] = useState(false);

  useEffect(() => {
    fetchDemoPersonas().then((d) => {
      if (d && d.enabled) setDemoPersonas(d.personas || []);
    });
  }, []);

  // Issue 1: an already-authenticated visitor never sees the sign-in page.
  // Clicking the logo or opening start.klareet.com sends them onward to the
  // same place a fresh login would, using the app's own landing logic.
  useEffect(() => {
    const token = getAccess();
    if (!token) return undefined;
    const role = roleFromToken(token);
    if (role === "institution_admin" || role === "institution_staff") { nav("/admin", { replace: true }); return undefined; }
    if (role === "content_admin") { nav("/admin/books", { replace: true }); return undefined; }
    if (role === "platform_admin") { nav("/admin/items", { replace: true }); return undefined; }
    let cancelled = false;
    resolveLanding(product).then((to) => { if (!cancelled) nav(to, { replace: true }); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async () => {
    setErr("");
    if (mode === "signup" && password.length < 12) {
      setErr("Use at least twelve characters for your passphrase.");
      return;
    }
    setLoading(true);
    try {
      if (mode === "signup") {
        await client.post("/auth/register", {
          email, password, role: "person",
          date_of_birth: dob || null,
          display_name: displayName || null,
        });
      }
      const { data } = await client.post("/auth/login", { email, password });
      // Stage 2: store only the access token; the refresh token stays in the
      // HttpOnly cookie the backend set on this response.
      setTokens(data.access_token, data.refresh_token);
      // Sign up always starts at consent; sign in resumes where you left off.
      if (mode === "signup") {
        nav(product === "grow" ? "/grow/consent" : "/consent");
      } else {
        const role = roleFromToken(data.access_token);
        if (role === "institution_admin" || role === "institution_staff") {
          nav("/admin");
        } else if (role === "content_admin") {
          nav("/admin/books");
        } else if (role === "platform_admin") {
          nav("/admin/items");
        } else {
          nav(await resolveLanding(product));
        }
      }
    } catch (e) {
      setErr(errText(e, "That did not go through. Give it another try."));
    } finally {
      setLoading(false);
    }
  };

  const tryPersona = async (p) => {
    setErr(""); setDemoLoading(p.key);
    try {
      const d = await demoLogin(p.key);
      const to = landingForPersona(d.product || p.product, d.state, d.role);
      nav(to);
    } catch (e) {
      setErr(errText(e, "Demo sign in did not work. Give it another try."));
    } finally {
      setDemoLoading(null);
    }
  };

  const productLabel = product === "grow" ? "Klareet Grow" : "Klareet Start";
  const helper = mode === "signin"
    ? "Log in to pick up your read and plan."
    : "Start free. An email and a passphrase, then we talk.";

  return (
    <div className="k-auth-shell" data-testid="auth-screen">
      {/* Quiet ambient layers behind the whole page. */}
      <div className="k-auth-grid" aria-hidden="true" />
      <div className="k-auth-grain" aria-hidden="true" />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-5 py-8 sm:py-14 z-10">
        <div className="grid lg:grid-cols-12 gap-6 lg:gap-10 lg:items-center">
          {/* IMMERSION PANEL */}
          <div className="lg:col-span-7 k-fade-in">
            <div className="k-auth-panel" data-testid="auth-panel">
              <div className="k-auth-panel-grid" aria-hidden="true" />
              <span className="k-panel-orb k-panel-orb-1" style={{ top: "-6rem", left: "-4rem", width: "20rem", height: "20rem" }} aria-hidden="true" />
              <span className="k-panel-orb k-panel-orb-2" style={{ bottom: "-7rem", right: "-3rem", width: "18rem", height: "18rem" }} aria-hidden="true" />
              <span className="k-panel-orb k-panel-orb-3" style={{ top: "40%", left: "38%", width: "14rem", height: "14rem" }} aria-hidden="true" />

              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-8">
                  <span className="k-panel-chip k-panel-chip-accent">
                    <Sparkles size={12} /> {productLabel}
                  </span>
                </div>

                <KineticHero />

                <p className="k-panel-sub mt-4 mb-7" data-testid="auth-subhead">
                  Seventeen honest questions with Klair. No quiz, no fake
                  scores. You leave knowing what fits you, what to do for
                  the next twelve weeks, and how to say it out loud in a room.
                </p>

                {/* Product preview: what a finished read looks like. */}
                <ReadPreview />

                {/* The four surfaces you actually walk away with. */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6" data-testid="auth-surfaces">
                  <PanelTile icon={<Layers size={14} />}   title="Your read"   hint="Who you are, six sides" />
                  <PanelTile icon={<Compass size={14} />}  title="Roadmap"     hint="Twelve weeks of next steps" />
                  <PanelTile icon={<FileText size={14} />} title="Resume"      hint="Built to get replies" />
                  <PanelTile icon={<Link2 size={14} />}    title="Share link"  hint="Private until you send it" />
                </div>

                {/* Three promises, kept quiet. */}
                <div className="mt-7 flex flex-wrap items-center gap-x-4 gap-y-2 k-panel-promise" data-testid="auth-principles">
                  <span className="inline-flex items-center gap-2">
                    <MessageCircle size={14} /> one question at a time
                  </span>
                  <span aria-hidden="true" className="k-panel-dot">·</span>
                  <span className="inline-flex items-center gap-2">
                    <ShieldCheck size={14} /> no fake scores
                  </span>
                  <span aria-hidden="true" className="k-panel-dot">·</span>
                  <span className="inline-flex items-center gap-2">
                    <BookOpen size={14} /> your words stay yours
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* ACTION COLUMN */}
          <div className="lg:col-span-5 k-fade-in" style={{ animationDelay: "80ms" }}>
            <div className="k-auth-card">
              <div className="p-6 sm:p-7">
                <div className="k-auth-seg" role="tablist" aria-label="Sign in or create account">
                  <button
                    role="tab"
                    aria-pressed={mode === "signin"}
                    onClick={() => setMode("signin")}
                    className="k-auth-seg-btn"
                    data-testid="mode-signin"
                  >
                    <LogIn size={14} /> Sign in
                  </button>
                  <button
                    role="tab"
                    aria-pressed={mode === "signup"}
                    onClick={() => setMode("signup")}
                    className="k-auth-seg-btn"
                    data-testid="mode-signup"
                  >
                    <UserPlus size={14} /> Create account
                  </button>
                </div>

                <p className="k-quiet mt-3" data-testid="auth-helper">{helper}</p>

                <form
                  className="mt-4 space-y-4"
                  onSubmit={(ev) => { ev.preventDefault(); submit(); }}
                >
                  <div>
                    <label className="k-label" htmlFor="auth-email">Email</label>
                    <input
                      id="auth-email"
                      type="email"
                      className="k-input mt-1"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      data-testid="auth-email"
                      placeholder="you@work.example"
                      autoComplete="email"
                      required
                    />
                  </div>
                  <div>
                    <label className="k-label" htmlFor="auth-password">Password</label>
                    <div className="k-input-wrap mt-1">
                      <input
                        id="auth-password"
                        type={showPassword ? "text" : "password"}
                        className="k-input k-input-pw"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        data-testid="auth-password"
                        placeholder="at least twelve characters"
                        autoComplete={mode === "signup" ? "new-password" : "current-password"}
                        required
                      />
                      <button
                        type="button"
                        className="k-input-eye"
                        onClick={() => setShowPassword((v) => !v)}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                        aria-pressed={showPassword}
                        data-testid="auth-password-toggle"
                        tabIndex={-1}
                      >
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                    {mode === "signin" && (
                      <div className="text-right mt-1.5">
                        <button
                          type="button"
                          onClick={() => nav("/forgot-password")}
                          className="k-quiet !text-xs hover:underline"
                          data-testid="auth-forgot-link"
                        >
                          Forgot your password?
                        </button>
                      </div>
                    )}
                  </div>
                  {mode === "signup" && (
                    <div className="space-y-4">
                      <div>
                        <label className="k-label" htmlFor="auth-name">Display name</label>
                        <input
                          id="auth-name"
                          className="k-input mt-1"
                          value={displayName}
                          onChange={(e) => setDisplayName(e.target.value)}
                          data-testid="auth-name"
                          placeholder="what Klair calls you"
                          autoComplete="given-name"
                        />
                      </div>
                      <div>
                        <label className="k-label">Date of birth</label>
                        <DobPicker value={dob} onChange={setDob} />
                      </div>
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={loading}
                    className="k-btn k-btn-accent w-full mt-1"
                    data-testid="auth-submit"
                  >
                    {loading ? "One moment." : mode === "signin" ? (
                      <><LogIn size={14} /> Continue</>
                    ) : (
                      <><Sparkles size={14} /> Create and begin</>
                    )}
                  </button>
                  {err && (
                    <div
                      className="text-sm px-3 py-2 rounded-lg"
                      style={{
                        color: "hsl(0 60% 40%)",
                        background: "hsl(0 60% 96%)",
                        border: "1px solid hsl(0 60% 88%)",
                      }}
                      data-testid="auth-error"
                    >
                      {err}
                    </div>
                  )}
                  <p className="k-quiet !text-xs">
                    By continuing you agree to a short data policy on the next
                    screen. Consent is opt in and you can pull it back any time.
                  </p>
                </form>
              </div>
            </div>

            {/* Demo drawer. Collapsed by default so the auth card is
                the primary action. Opens with a soft slide. */}
            {demoPersonas && demoPersonas.length > 0 && (
              <div className="mt-4 k-fade-in" style={{ animationDelay: "160ms" }}>
                <button
                  type="button"
                  onClick={() => setDemoOpen((v) => !v)}
                  className="w-full flex items-center justify-between gap-3 p-3 rounded-xl"
                  style={{
                    background: "hsl(var(--klareet-canvas-2))",
                    border: "1px solid hsl(var(--klareet-line-soft))",
                    color: "hsl(var(--klareet-ink))",
                  }}
                  aria-expanded={demoOpen}
                  aria-controls="demo-panel"
                  data-testid="demo-toggle"
                >
                  <span className="inline-flex items-center gap-2 text-sm font-semibold">
                    <Zap size={14} style={{ color: "hsl(var(--klareet-amber))" }} />
                    Peek inside a seeded persona
                  </span>
                  <ChevronDown
                    size={16}
                    className="k-demo-chev"
                    data-open={demoOpen ? "true" : "false"}
                  />
                </button>

                {demoOpen && (
                  <div
                    id="demo-panel"
                    className="k-card mt-2"
                    data-testid="demo-panel"
                    style={{ maxHeight: "22rem", overflowY: "auto" }}
                  >
                    <p className="k-quiet !text-xs mb-2">
                      One tap. These personas are seeded in the development
                      database and are hidden in production.
                    </p>
                    <ul className="space-y-2">
                      {demoPersonas.map((p) => (
                        <li key={p.key}>
                          <button
                            onClick={() => tryPersona(p)}
                            disabled={demoLoading !== null}
                            className="w-full flex items-center justify-between gap-3 p-3 rounded-lg border transition-colors text-left"
                            style={{
                              borderColor: "hsl(var(--klareet-line))",
                              background: demoLoading === p.key
                                ? "hsl(var(--klareet-blue-soft))"
                                : "hsl(var(--klareet-canvas-2))",
                            }}
                            data-testid={`demo-persona-${p.key}`}
                          >
                            <div className="min-w-0">
                              <div
                                className="text-sm font-semibold truncate"
                                style={{ color: "hsl(var(--klareet-ink))" }}
                              >
                                {p.label}
                              </div>
                              <div className="k-quiet !text-xs truncate">{p.hint}</div>
                              <div className="flex gap-1 mt-1">
                                {p.state?.consent && <span className="klareet-mono !text-[10px]" style={{ color: "hsl(var(--klareet-teal))" }}>consent</span>}
                                {p.state?.intake  && <span className="klareet-mono !text-[10px]" style={{ color: "hsl(var(--klareet-teal))" }}>intake</span>}
                                {p.state?.read    && <span className="klareet-mono !text-[10px]" style={{ color: "hsl(var(--klareet-teal))" }}>read</span>}
                                {p.state?.paid    && <span className="klareet-mono !text-[10px]" style={{ color: "hsl(var(--klareet-blue-strong))" }}>paid</span>}
                                {p.is_institution_admin && <span className="klareet-mono !text-[10px]" style={{ color: "hsl(var(--klareet-amber))" }}>admin</span>}
                                {p.is_content_admin && <span className="klareet-mono !text-[10px]" style={{ color: "hsl(var(--klareet-amber))" }}>panel</span>}
                              </div>
                            </div>
                            <ArrowRight size={16} style={{ color: "hsl(var(--klareet-muted))" }} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


/**
 * KineticHero
 *
 * One headline for both modes. It holds a stable frame with a
 * cycling phrase inside, so a returning caller and a first timer
 * see the same clear promise, with motion.
 */
function KineticHero() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % KINETIC_WORDS.length), 3800);
    return () => clearInterval(t);
  }, []);
  const word = KINETIC_WORDS[i];
  return (
    <h1 className="k-panel-title" data-testid="auth-title">
      Find your next move,
      <br />
      <span className="k-panel-title-accent">
        and{" "}
        <span className="k-kinetic-word">
          <span key={i}>{word}</span>
        </span>.
      </span>
    </h1>
  );
}


/**
 * ReadPreview
 *
 * A bright product card that shows what a finished read looks like:
 * an archetype and its layer signals. It is tagged as a sample so it
 * never reads as a real result, in line with the no-fabrication rule.
 */
function ReadPreview() {
  return (
    <div className="k-read-preview" data-testid="auth-read-preview">
      <div className="flex items-center justify-between mb-3">
        <span className="k-read-eyebrow">Your read</span>
        <span className="k-read-sample">sample</span>
      </div>
      <div className="flex items-end justify-between gap-3 mb-3">
        <div>
          <div className="k-read-arch">The Builder</div>
          <div className="k-quiet !text-xs">a reachable goal, with support</div>
        </div>
        <span className="k-read-badge">6 sides</span>
      </div>
      <div className="k-spectrum-bar mb-4" style={{ borderRadius: 999, height: 6 }} aria-hidden="true" />
      <div className="space-y-2.5">
        {READ_LAYERS.map((l) => (
          <div className="k-read-row" key={l.label} data-testid={`read-layer-${l.label.toLowerCase()}`}>
            <span className="k-read-label">{l.label}</span>
            <span className="k-read-track">
              <span
                className="k-read-fill"
                style={{ width: `${l.pct}%`, background: `hsl(${l.tone})` }}
              />
            </span>
          </div>
        ))}
      </div>
      <div className="k-read-foot">Next: a twelve week plan and a resume.</div>
    </div>
  );
}


function PanelTile({ icon, title, hint }) {
  return (
    <div className="k-panel-tile" data-testid={`surface-${title.toLowerCase().replaceAll(" ", "-")}`}>
      <span className="k-panel-tile-icon">{icon}</span>
      <div className="k-panel-tile-title">{title}</div>
      <div className="k-panel-tile-hint">{hint}</div>
    </div>
  );
}



/**
 * DobPicker
 *
 * A modern date-of-birth control: three dropdowns for month, day,
 * and year. Faster than a calendar for a birthday because you never
 * click back through decades. It emits an ISO date (YYYY-MM-DD) so
 * the backend contract is unchanged, and clamps the day when the
 * selected month or year cannot hold it (for example February 29).
 */
function DobPicker({ value, onChange }) {
  const parsed = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || "");
  const [year, setYear] = useState(parsed ? parsed[1] : "");
  const [month, setMonth] = useState(parsed ? String(parseInt(parsed[2], 10)) : "");
  const [day, setDay] = useState(parsed ? String(parseInt(parsed[3], 10)) : "");

  const thisYear = new Date().getFullYear();
  const years = [];
  for (let y = thisYear; y >= thisYear - 100; y--) years.push(y);

  const daysFor = (mm, yy) => {
    if (!mm) return 31;
    // Fall back to a leap year when the year is unset so 29 stays selectable.
    return new Date(parseInt(yy, 10) || 2000, parseInt(mm, 10), 0).getDate();
  };
  const days = Array.from({ length: daysFor(month, year) }, (_, i) => i + 1);

  const emit = (yy, mm, dd) => {
    onChange(yy && mm && dd ? `${yy}-${pad2(mm)}-${pad2(dd)}` : "");
  };

  const onMonth = (e) => {
    const mm = e.target.value;
    let dd = day;
    if (dd && parseInt(dd, 10) > daysFor(mm, year)) dd = "";
    setMonth(mm); setDay(dd); emit(year, mm, dd);
  };
  const onDay = (e) => {
    const dd = e.target.value;
    setDay(dd); emit(year, month, dd);
  };
  const onYear = (e) => {
    const yy = e.target.value;
    let dd = day;
    if (dd && parseInt(dd, 10) > daysFor(month, yy)) dd = "";
    setYear(yy); setDay(dd); emit(yy, month, dd);
  };

  return (
    <div className="grid grid-cols-[1.4fr_1fr_1.1fr] gap-2 mt-1" data-testid="auth-dob">
      <select
        className="k-input k-select"
        value={month}
        onChange={onMonth}
        aria-label="Birth month"
        autoComplete="bday-month"
        data-testid="auth-dob-month"
      >
        <option value="">Month</option>
        {MONTHS.map((mn, i) => (
          <option key={mn} value={i + 1}>{mn}</option>
        ))}
      </select>
      <select
        className="k-input k-select"
        value={day}
        onChange={onDay}
        aria-label="Birth day"
        autoComplete="bday-day"
        data-testid="auth-dob-day"
      >
        <option value="">Day</option>
        {days.map((dn) => (
          <option key={dn} value={dn}>{dn}</option>
        ))}
      </select>
      <select
        className="k-input k-select"
        value={year}
        onChange={onYear}
        aria-label="Birth year"
        autoComplete="bday-year"
        data-testid="auth-dob-year"
      >
        <option value="">Year</option>
        {years.map((yr) => (
          <option key={yr} value={yr}>{yr}</option>
        ))}
      </select>
    </div>
  );
}